// fileProcessingUtils.js - Comprehensive file processing utility
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';

/**
 * Extract text from DOCX files using mammoth
 * @param {File} file - The DOCX file to process
 * @returns {Promise<{text: string, html: string, wordCount: number}>} Extracted content
 */
export const extractDocxContent = async (file) => {
  try {
    // Get buffer data - handle both original Files and copied file objects
    let arrayBuffer;
    if (typeof file.arrayBuffer === 'function') {
      // Original File object
      arrayBuffer = await file.arrayBuffer();
    } else if (file.buffer) {
      // Copied file object with buffer property
      arrayBuffer = file.buffer;
    } else {
      throw new Error("Cannot access file data: no arrayBuffer method or buffer property");
    }
    
    // Use mammoth to extract both text and HTML
    const textResult = await mammoth.extractRawText({ arrayBuffer });
    const htmlResult = await mammoth.convertToHtml({ arrayBuffer });
    
    // Get the extracted text content
    const textContent = textResult.value;
    const htmlContent = htmlResult.value;
    
    // Calculate word count (rough approximation)
    const wordCount = textContent.split(/\s+/).filter(word => word.length > 0).length;
    
    console.log(`Successfully extracted DOCX content from ${file.name}: ${wordCount} words`);
    
    return {
      text: textContent,
      html: htmlContent,
      wordCount,
      success: true
    };
  } catch (error) {
    console.error(`Error extracting content from DOCX file ${file.name}:`, error);
    return {
      text: `[Error extracting DOCX content: ${error.message}]`,
      html: '',
      wordCount: 0,
      success: false,
      error: error.message
    };
  }
};

/**
 * Extract content from Excel spreadsheets (XLSX, XLS, etc.)
 * @param {File} file - The spreadsheet file to process
 * @returns {Promise<{text: string, sheets: Object, rowCount: number}>} Extracted content
 */
export const extractSpreadsheetContent = async (file) => {
  try {
    // Get buffer data - handle both original Files and copied file objects
    let arrayBuffer;
    if (typeof file.arrayBuffer === 'function') {
      // Original File object
      arrayBuffer = await file.arrayBuffer();
    } else if (file.buffer) {
      // Copied file object with buffer property
      arrayBuffer = file.buffer;
    } else {
      throw new Error("Cannot access file data: no arrayBuffer method or buffer property");
    }
    
    // Read the workbook with full options
    const workbook = XLSX.read(arrayBuffer, {
      type: 'array',
      cellDates: true,
      cellStyles: true,
      cellFormulas: true
    });
    
    // Get all sheet names
    const sheetNames = workbook.SheetNames;
    const sheets = {};
    let totalRows = 0;
    let textContent = `Spreadsheet: ${file.name}\n\nSheets: ${sheetNames.join(', ')}\n\n`;
    
    // Process each sheet
    sheetNames.forEach((sheetName) => {
      // Convert sheet to JSON
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      
      // Store sheet data
      sheets[sheetName] = jsonData;
      
      // Count rows with content
      const nonEmptyRows = jsonData.filter(row => row.some(cell => cell !== ''));
      totalRows += nonEmptyRows.length;
      
      // Add sheet content to text representation
      textContent += `Sheet: ${sheetName} (${nonEmptyRows.length} rows)\n`;
      
      // Format sheet data as a table (limited to first 20 rows for size constraints)
      const visibleRows = jsonData.slice(0, 20);
      if (visibleRows.length > 0) {
        // Get column headers or generate generic ones
        const headers = visibleRows[0].map((_, i) => 
          typeof visibleRows[0][i] !== 'undefined' ? visibleRows[0][i].toString() : `Column ${i+1}`
        );
        
        // Add headers to text content
        textContent += `\n${headers.join('\t')}\n`;
        
        // Add a separator line
        textContent += `${headers.map(() => '-------').join('\t')}\n`;
        
        // Add data rows (skip header if it looks like a header row)
        const dataRows = headers.some(h => typeof h === 'string' && h.trim() !== '') ? 
                          visibleRows.slice(1) : visibleRows;
        
        dataRows.forEach(row => {
          textContent += `${row.map(cell => 
            typeof cell !== 'undefined' ? cell.toString() : '').join('\t')}\n`;
        });
        
        // Add note if there are more rows
        if (jsonData.length > 20) {
          textContent += `\n[Showing 20/${jsonData.length} rows]\n`;
        }
      }
      
      textContent += "\n\n";
    });
    
    console.log(`Successfully extracted spreadsheet content from ${file.name}: ${totalRows} total rows across ${sheetNames.length} sheets`);
    
    return {
      text: textContent,
      sheets,
      sheetNames,
      rowCount: totalRows,
      success: true
    };
  } catch (error) {
    console.error(`Error extracting content from spreadsheet file ${file.name}:`, error);
    return {
      text: `[Error extracting spreadsheet content: ${error.message}]`,
      sheets: {},
      sheetNames: [],
      rowCount: 0,
      success: false,
      error: error.message
    };
  }
};

/**
 * Extract content from text-based files (TXT, CSV, etc.)
 * @param {File} file - The text file to process
 * @returns {Promise<{text: string, lineCount: number}>} Extracted content
 */
export const extractTextContent = async (file) => {
  try {
    // Read file as text
    const text = await file.text();
    
    // Count lines
    const lineCount = text.split('\n').length;
    
    console.log(`Successfully extracted text content from ${file.name}: ${lineCount} lines`);
    
    return {
      text,
      lineCount,
      success: true
    };
  } catch (error) {
    console.error(`Error extracting content from text file ${file.name}:`, error);
    return {
      text: `[Error extracting text content: ${error.message}]`,
      lineCount: 0,
      success: false,
      error: error.message
    };
  }
};

/**
 * Process a file to ensure content is extracted
 * @param {File} file - The file to process
 * @returns {Promise<Object>} Processed file with content
 */
export const processFile = async (file) => {
  // Create a copy of the file with all properties
  const processedFile = { ...file };
  
  // Try to read file content based on type
  try {
    // Determine file type from extension if MIME type is generic
    const extension = file.name.split('.').pop().toLowerCase();
    
    // Process based on file type
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        extension === 'docx') {
      // DOCX files
      processedFile.isDocx = true;
      const result = await extractDocxContent(file);
      processedFile.text = result.text;
      processedFile.html = result.html;
      processedFile.wordCount = result.wordCount;
      processedFile.content = result.text;
      processedFile.extractionSuccess = result.success;
    } 
    else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
             file.type === 'application/vnd.ms-excel' ||
             ['xlsx', 'xls', 'csv'].includes(extension)) {
      // Excel files
      processedFile.isSpreadsheet = true;
      const result = await extractSpreadsheetContent(file);
      processedFile.text = result.text;
      processedFile.sheets = result.sheets;
      processedFile.sheetNames = result.sheetNames;
      processedFile.rowCount = result.rowCount;
      processedFile.content = result.text;
      processedFile.extractionSuccess = result.success;
    }
    else if (file.type.startsWith('text/') || 
             ['txt', 'md', 'json', 'csv', 'log', 'xml', 'html', 'css', 'js', 'ts', 'py', 'java'].includes(extension)) {
      // Text files
      processedFile.isText = true;
      const result = await extractTextContent(file);
      processedFile.text = result.text;
      processedFile.lineCount = result.lineCount;
      processedFile.content = result.text;
      processedFile.extractionSuccess = result.success;
    }
    else if (file.type.startsWith('image/')) {
      // Image files - just note we can't extract text
      processedFile.isImage = true;
      processedFile.text = `[Image file: ${file.name}]`;
      processedFile.content = processedFile.text;
      processedFile.extractionSuccess = true;
    }
    else {
      // Unknown/binary files
      processedFile.text = `[Binary file: ${file.name} (${file.type})]`;
      processedFile.content = processedFile.text;
      processedFile.extractionSuccess = false;
    }
    
    // Log success
    console.log(`File processing completed for ${file.name} (${file.type})`);
    return processedFile;
    
  } catch (err) {
    console.error(`Error processing file ${file.name}:`, err);
    processedFile.text = `[Error processing file: ${err.message}]`;
    processedFile.content = processedFile.text;
    processedFile.extractionSuccess = false;
    return processedFile;
  }
};

/**
 * Process multiple files to ensure content is extracted
 * @param {Array<File>} files - Array of files to process
 * @returns {Promise<Array<Object>>} Processed files with content
 */
export const processFiles = async (files) => {
  console.log(`Processing ${files.length} files...`);
  
  const processedFiles = [];
  
  for (const file of files) {
    try {
      const processedFile = await processFile(file);
      processedFiles.push(processedFile);
    } catch (err) {
      console.error(`Error in batch processing for file ${file.name}:`, err);
      // Add file with error info
      processedFiles.push({
        ...file,
        text: `[Error processing file: ${err.message}]`,
        content: `[Error processing file: ${err.message}]`,
        extractionSuccess: false
      });
    }
  }
  
  console.log(`Completed processing ${processedFiles.length} files`);
  return processedFiles;
};

/**
 * Format file content for Claude API
 * @param {Object} file - Processed file with content
 * @returns {string} Formatted content for Claude
 */
export const formatFileContentForClaude = (file) => {
  if (!file) return '';
  
  const extension = file.name.split('.').pop().toLowerCase();
  let formattedContent = `\n\nFile: ${file.name} (${file.type || extension}, ${(file.size / 1024).toFixed(2)}KB)\n`;
  
  if (file.isDocx) {
    // Format DOCX content
    if (file.extractionSuccess && file.text) {
      formattedContent += `\nDocument content:\n\n${file.text}\n\n`;
      if (file.wordCount) {
        formattedContent += `Word count: ${file.wordCount}\n`;
      }
    } else {
      formattedContent += `\n[DOCX content could not be extracted]\n`;
    }
  } 
  else if (file.isSpreadsheet) {
    // Format spreadsheet content
    if (file.extractionSuccess && file.text) {
      formattedContent += `\nSpreadsheet content:\n\n${file.text}\n`;
      if (file.rowCount) {
        formattedContent += `Total rows: ${file.rowCount}\n`;
      }
    } else {
      formattedContent += `\n[Spreadsheet content could not be extracted]\n`;
    }
  }
  else if (file.isText) {
    // Format text content
    if (file.extractionSuccess && file.text) {
      // Use code block formatting with language from extension
      let language = 'text';
      switch (extension) {
        case 'js': language = 'javascript'; break;
        case 'ts': language = 'typescript'; break;
        case 'py': language = 'python'; break;
        case 'java': language = 'java'; break;
        case 'html': language = 'html'; break;
        case 'css': language = 'css'; break;
        case 'json': language = 'json'; break;
        case 'xml': language = 'xml'; break;
        case 'md': language = 'markdown'; break;
        case 'csv': language = 'csv'; break;
        default: language = extension;
      }
      
      formattedContent += `\nContent:\n\`\`\`${language}\n${file.text}\n\`\`\`\n`;
      if (file.lineCount) {
        formattedContent += `Line count: ${file.lineCount}\n`;
      }
    } else {
      formattedContent += `\n[Text content could not be extracted]\n`;
    }
  }
  else if (file.isImage) {
    // Image files
    formattedContent += `\n[Image file - please use visual interpretation to analyze]\n`;
  }
  else if (file.type.startsWith('application/pdf') || extension === 'pdf') {
    // PDF files - handled separately in API
    formattedContent += `\n[PDF file - will be processed separately by Claude]\n`;
  }
  else {
    // Unknown/binary files
    formattedContent += `\n[Binary file - content cannot be displayed]\n`;
  }
  
  return formattedContent;
};